import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getUnixTime, parseJSON } from 'date-fns';
import _ from 'lodash';

import './TillHistory.scss';
import TillHistoryTable from './components/TillHistoryTable/TillHistoryTable';
import { DateRangeProps } from 'src/components/RangePicker/RangePicker';
import TillFilters from './components/TillFilters/TillFilters';
import TransactionContext from '../../context/Transactions.context';
import { RootState } from 'src/configs/store';
import classNames from 'classnames';
import AdminFilter from './components/TillFilters/AdminFilter';

interface TillHistoryProps {
  loginTime: number,
  adminSession?: string | null
  defaultOrder?: string
}

function TillHistory({ loginTime, adminSession=null, defaultOrder='Asc' }: TillHistoryProps) {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const { filterType, setFilterType, setDates } = useContext(TransactionContext);
  const { shouldReload } = useSelector((state: RootState) => state.peripherals);
  const [selectedOptions, setSelectedOptions] = useState({ limit: 10, order: defaultOrder });
  const [filters, setFilters] = useState([]);
  const [query, setQuery] = useState({});
  const [selectedFilters, setSelectedFilters] = useState([]);

  const [dateRange, setDateRange] = useState<DateRangeProps[]>([{
    startDate: parseJSON(loginTime * 1000),
    endDate: new Date(),
    key: 'selection'
  }]);
  const [transactions, setTransactions] = useState(null);
  const [showMoreButton, setShowMoreButton] = useState(false);
  const dispatch = useDispatch();

  const loadData = async () => {
    const data = !adminSession ? await dispatch.peripherals.getTillTransactionHistory({
      offset: offset * selectedOptions.limit,
      createdAtFrom: getUnixTime(dateRange[0].startDate),
      transactionType: filterType,
      limit: selectedOptions.limit,
      order: selectedOptions.order.toLowerCase()
    }) : await dispatch.peripherals.getAdminTillTransactionHistory({
      offset: offset * selectedOptions.limit,
      createdAtFrom: getUnixTime(dateRange[0].startDate),
      session: adminSession,
      transactionType: filterType,
      limit: selectedOptions.limit,
      order: selectedOptions.order.toLowerCase(),
      ...query
    });
    const newData = data.collection ? data.collection : data;

    if (data.searchFields){
      setFilters(data.searchFields);
    }

    if (offset > 0){
      setTransactions([...transactions, ...newData]);
    } else {
      setTransactions(newData);
    }

    setDates({
      startDate: getUnixTime(dateRange[0].startDate),
      endDate: getUnixTime(new Date())
    });
    setShowMoreButton(data.length >= 10);
  };

  const changeSelectedOptions = useCallback(async (key, value) => {
    setSelectedOptions({ ...selectedOptions, [key]: value });
    const newOffset = key === 'limit' ? 0: offset * selectedOptions.limit;
    const data = !adminSession ? await dispatch.peripherals.getTillTransactionHistory({
      offset: newOffset,
      createdAtFrom: getUnixTime(dateRange[0].startDate),
      transactionType: filterType,
      limit: key === 'limit' ? value : selectedOptions.limit,
      order: key === 'order' ? value.toLowerCase() : selectedOptions.order.toLowerCase()
    }) : await dispatch.peripherals.getAdminTillTransactionHistory({
      offset: newOffset,
      session: adminSession,
      createdAtFrom: getUnixTime(dateRange[0].startDate),
      transactionType: filterType,
      limit: key === 'limit' ? value : selectedOptions.limit,
      order: key === 'order' ? value.toLowerCase() : selectedOptions.order.toLowerCase(),
      ...query
    });

    if (offset > 0){
      setTransactions([...transactions, ...data]);
    } else {
      setTransactions(data);
    }

    setDates({
      startDate: getUnixTime(dateRange[0].startDate),
      endDate: getUnixTime(new Date())
    });
    setShowMoreButton(data.length >= 10);

  }, []);

  useEffect(() => {
    if (!shouldReload){
      loadData();
    }
  }, [dateRange, filterType, offset, query]);

  const getDataAfterTransaction = async () => {
    const data = !adminSession ? await dispatch.peripherals.getTillTransactionHistory({
      offset: 0,
      createdAtFrom: getUnixTime(parseJSON(loginTime * 1000)),
      transactionType: filterType,
      limit: selectedOptions.limit,
      order: selectedOptions.order.toLowerCase()
    }) : await dispatch.peripherals.getAdminTillTransactionHistory({
      offset: 0,
      createdAtFrom: getUnixTime(parseJSON(loginTime * 1000)),
      transactionType: filterType,
      session: adminSession,
      limit: selectedOptions.limit,
      order: selectedOptions.order.toLowerCase(),
      ...query
    });
    setTransactions(data);
    setShowMoreButton(data.length >= 10);
  };

  useEffect(() => {
    if (shouldReload){
      setOffset(0);
      setTransactions([]);
      setTimeout(() => {
        getDataAfterTransaction();
      }, 100);
    }
  }, [shouldReload]);

  const changeTransactionType = useCallback((value) => {
    setTransactions([]);
    setOffset(0);
    setFilterType(value);
  }, []);

  const changeDate = useCallback((value) => {
    setTransactions([]);
    setOffset(0);
    setDateRange(value);
  }, []);

  const addQuery = _.debounce((value, name) => {
    setQuery({ [name]: value });
    if (value !== ''){
      setSelectedFilters([...selectedFilters, name]);
    } else {
      const oldQuery = JSON.parse(JSON.stringify(query));
      oldQuery.delete(name);
      const newFilters = _.filter(selectedFilters, (filter) => filter !== name);
      setSelectedFilters(newFilters);
    }
  }, 1000);

  return (
    <div className={
      classNames({
        'till-history' : true,
        'till-history__admin': adminSession
      })
    }
    >
      { !adminSession && <h4 className="till-history__title">{ !adminSession ? t('transactions.tillHistory') : t('transactions.adminHistory') }</h4> }

      {
        !adminSession &&
      <TillFilters
        loginTime={ loginTime }
        filterType={ filterType }
        dateRange={ dateRange }
        setDateRange={ changeDate }
        setFilterType={ changeTransactionType }
        changeOptions= { changeSelectedOptions }
        selectedOptions={ selectedOptions }
      />
      }
      { adminSession && transactions && <AdminFilter filters={ filters } addToFilter={ addQuery } selectedFilters={ selectedFilters } /> }
      <div className="till-history__footer">
        <TillHistoryTable transactions={ transactions } isAdmin={ adminSession } />
        {
          showMoreButton && <button
            className="till-history__show-more"
            onClick={ () => setOffset(offset +1) }
          >{ t('transactions.showMore') }</button>
        }
      </div>
    </div>
  );
}

export default TillHistory;
